import React, {forwardRef, useEffect, useState} from "react";

const SWITCH_COLOR_MAP = {
    danger: "#fb404b",
    default: "#b8bec7",
    info: "#447df7",
    primary: "#447df7",
    success: "#87cb16",
    warning: "#ffa534"
};

function normalizeLabel(label, fallback) {
    if (label === undefined || label === null) {
        return fallback;
    }

    return label;
}

function getLabelLength(label) {
    if (typeof label === "string") {
        return label.trim().length;
    }

    if (typeof label === "number") {
        return String(label).length;
    }

    return 0;
}

function getTrackColor(checked, onColor, offColor) {
    const colorKey = checked ? onColor : offColor;

    if (SWITCH_COLOR_MAP[colorKey]) {
        return SWITCH_COLOR_MAP[colorKey];
    }

    return checked ? SWITCH_COLOR_MAP.info : SWITCH_COLOR_MAP.default;
}

const Switch = forwardRef((props, ref) => {
    const {
        className = "",
        defaultValue = true,
        disabled = false,
        id,
        name,
        offColor = "default",
        offText,
        onBlur,
        onColor = "info",
        onChange,
        onFocus,
        onText,
        style,
        tabIndex,
        title,
        value,
    } = props;

    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = useState(Boolean(defaultValue));

    useEffect(() => {
        if (!isControlled) {
            setInternalValue(Boolean(defaultValue));
        }
    }, [defaultValue, isControlled]);

    const checked = isControlled ? Boolean(value) : internalValue;
    const onLabel = normalizeLabel(onText, "ON");
    const offLabel = normalizeLabel(offText, "OFF");
    const longestLabelLength = Math.max(getLabelLength(onLabel), getLabelLength(offLabel));
    const switchWidth = longestLabelLength === 0 ? 48 : Math.max(76, 52 + longestLabelLength * 7);
    const trackPadding = 4;
    const handleSize = 22;
    const inlineStyle = {
        ...style,
        "--switch-handle-size": `${handleSize}px`,
        "--switch-height": "30px",
        "--switch-knob-offset": `${switchWidth - handleSize - (trackPadding * 2)}px`,
        "--switch-padding": `${trackPadding}px`,
        "--switch-track-color": getTrackColor(checked, onColor, offColor),
        "--switch-width": `${switchWidth}px`
    };
    const switchClasses = [
        "custom-bootstrap-switch",
        checked ? "custom-bootstrap-switch-on" : "custom-bootstrap-switch-off",
        disabled ? "custom-bootstrap-switch-disabled" : "",
        className
    ].filter(Boolean).join(" ");

    const handleToggle = () => {
        if (disabled) {
            return;
        }

        const nextValue = !checked;

        if (!isControlled) {
            setInternalValue(nextValue);
        }

        if (onChange) {
            const changePayload = {
                currentTarget: {
                    checked: nextValue,
                    id,
                    name,
                    value: nextValue
                },
                props: {
                    checked: nextValue,
                    id,
                    name,
                    value: nextValue
                },
                target: {
                    checked: nextValue,
                    id,
                    name,
                    value: nextValue
                },
                type: "change"
            };

            onChange(changePayload, nextValue);
        }
    };

    return (
        <button
            aria-checked={checked}
            aria-label={title || name || "Toggle switch"}
            aria-pressed={checked}
            className={switchClasses}
            disabled={disabled}
            id={id}
            name={name}
            onBlur={onBlur}
            onClick={handleToggle}
            onFocus={onFocus}
            ref={ref}
            role="switch"
            style={inlineStyle}
            tabIndex={tabIndex}
            title={title}
            type="button"
        >
            <span className="custom-bootstrap-switch-track">
                <span className="custom-bootstrap-switch-option custom-bootstrap-switch-option-on">
                    {onLabel}
                </span>
                <span className="custom-bootstrap-switch-handle"/>
                <span className="custom-bootstrap-switch-option custom-bootstrap-switch-option-off">
                    {offLabel}
                </span>
            </span>
        </button>
    );
});

Switch.displayName = "Switch";

export default Switch;
